import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { ConfirmDialog, Toast, useToast, useConfirm } from '../components/ConfirmDialog';
import { MdSearch, MdFilterList, MdEdit, MdDelete, MdCalendarToday, MdWarning, MdAdd, MdCheckCircle, MdRadioButtonUnchecked, MdPlayArrow } from 'react-icons/md';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import './Tasks.css';

const Tasks = () => {
  const { user } = useContext(AuthContext);
  const { toasts, addToast, removeToast } = useToast();
  const { dialog, confirm } = useConfirm();
  const isAdmin = user?.role === 'admin';
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project_id: '',
    assigned_to: '',
    priority: 'medium',
    status: 'todo',
    due_date: ''
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');

  useEffect(() => {
    fetchTasks();
    fetchProjects();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    filterTasks();
  }, [tasks, searchTerm, priorityFilter, projectFilter]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get('/api/tasks');
      // Filter tasks for regular users (only assigned to them)
      const allTasks = response.data;
      const userTasks = user?.role === 'admin' 
        ? allTasks 
        : allTasks.filter(t => t.assigned_to === user?.id);
      setTasks(userTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const filterTasks = () => {
    let filtered = [...tasks];

    if (searchTerm) {
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }

    if (projectFilter !== 'all') {
      filtered = filtered.filter(task => task.project_id === parseInt(projectFilter));
    }

    setFilteredTasks(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await axios.put(`/api/tasks/${editingTask.id}`, formData);
      } else {
        await axios.post('/api/tasks', formData);
      }
      setShowModal(false);
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        project_id: '',
        priority: 'medium',
        status: 'todo',
        due_date: ''
      });
      fetchTasks();
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      project_id: task.project_id || '',
      assigned_to: task.assigned_to || '',
      priority: task.priority,
      status: task.status,
      due_date: task.due_date ? task.due_date.split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleDelete = async (taskId) => {
    const ok = await confirm('Delete Task', 'Are you sure you want to delete this task?', { confirmText: 'Delete', confirmColor: '#EF4444' });
    if (!ok) return;
    try {
      await axios.delete(`/api/tasks/${taskId}`);
      addToast('Task deleted successfully', 'success');
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      addToast('Failed to delete task', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      project_id: '',
      assigned_to: '',
      priority: 'medium',
      status: 'todo',
      due_date: ''
    });
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`/api/tasks/${taskId}`, { status: newStatus });
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) return;

    const taskId = parseInt(draggableId.split('-')[1]);
    const newStatus = destination.droppableId;

    // Optimistically update local state so UI doesn't snap back
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId ? { ...task, status: newStatus } : task
      )
    );

    // Sync with backend
    try {
      await axios.put(`/api/tasks/${taskId}`, { status: newStatus });
    } catch (error) {
      console.error('Error updating task status:', error);
      fetchTasks(); // Revert on failure
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const groupedTasks = {
    todo: filteredTasks.filter(t => t.status === 'todo'),
    in_progress: filteredTasks.filter(t => t.status === 'in_progress'),
    completed: filteredTasks.filter(t => t.status === 'completed')
  };

  if (loading) {
    return (
      <div className="app-container">
        <Sidebar />
        <div className="main-content tasks-main">
          <div className="tasks-page">
            <div className="page-header">
              <h1>Tasks</h1>
            </div>
            <div className="skeleton-board">
              {[1, 2, 3].map(i => (
                <div key={i} className="skeleton-column">
                  <div className="skeleton-header"></div>
                  <div className="skeleton-card"></div>
                  <div className="skeleton-card"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <Toast toasts={toasts} removeToast={removeToast} />
      <ConfirmDialog {...dialog} />
      <div className="main-content tasks-main">
        <div className="tasks-page">
          <div className="page-header">
            <div className="header-content">
              <h1>Tasks</h1>
              <p className="header-subtitle">{user?.role === 'admin' ? 'Manage and track your tasks across projects' : 'View and manage your assigned tasks'}</p>
            </div>
            <div className="header-actions">
              <span className="count-badge">{filteredTasks.length} {filteredTasks.length === 1 ? 'Task' : 'Tasks'}</span>
              {user?.role === 'admin' && (
                <button className="btn-primary" onClick={() => setShowModal(true)}>
                  <MdAdd /> New Task
                </button>
              )}
            </div>
          </div>

          <div className="filters-bar">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <MdFilterList className="filter-icon" />
              <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                <option value="all">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div className="filter-group">
              <MdFilterList className="filter-icon" />
              <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                <option value="all">All Projects</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="tasks-board">
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="task-column todo-column">
                <div className="column-header">
                  <div className="column-title">
                    <MdRadioButtonUnchecked className="column-icon" />
                    <h3>To Do</h3>
                  </div>
                  <span className="task-count">{groupedTasks.todo.length}</span>
                </div>
                <Droppable droppableId="todo">
                  {(provided, snapshot) => (
                    <div
                      className={`task-cards ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {groupedTasks.todo.map((task, index) => (
                        <Draggable key={task.id} draggableId={`task-${task.id}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`task-card ${isOverdue(task.due_date) ? 'overdue' : ''} ${snapshot.isDragging ? 'dragging' : ''}`}
                            >
                              <div className="task-card-header">
                                <h4>{task.title}</h4>
                                {isAdmin && (
                                  <div className="task-actions">
                                    <button className="icon-btn edit" onClick={() => handleEdit(task)} title="Edit">
                                      <MdEdit />
                                    </button>
                                    <button className="icon-btn delete" onClick={() => handleDelete(task.id)} title="Delete">
                                      <MdDelete />
                                    </button>
                                  </div>
                                )}
                              </div>
                              <p>{task.description}</p>
                              {task.due_date && (
                                <div className={`due-date ${isOverdue(task.due_date) ? 'overdue' : ''}`}>
                                  {isOverdue(task.due_date) && <MdWarning />}
                                  <MdCalendarToday />
                                  <span>{formatDueDate(task.due_date)}</span>
                                </div>
                              )}
                              <div className="task-meta">
                                <span className={`priority-badge priority-${task.priority}`}>
                                  {task.priority}
                                </span>
                                <span className="project-name">{task.project_name || 'No project'}</span>
                                {task.assigned_to_name && (
                                  <span className="assigned-to">👤 {task.assigned_to_name}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>

              <div className="task-column progress-column">
                <div className="column-header">
                  <div className="column-title">
                    <MdPlayArrow className="column-icon" />
                    <h3>In Progress</h3>
                  </div>
                  <span className="task-count">{groupedTasks.in_progress.length}</span>
                </div>
                <Droppable droppableId="in_progress">
                  {(provided, snapshot) => (
                    <div
                      className={`task-cards ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {groupedTasks.in_progress.map((task, index) => (
                        <Draggable key={task.id} draggableId={`task-${task.id}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`task-card ${isOverdue(task.due_date) ? 'overdue' : ''} ${snapshot.isDragging ? 'dragging' : ''}`}
                            >
                              <div className="task-card-header">
                                <h4>{task.title}</h4>
                                {isAdmin && (
                                  <div className="task-actions">
                                    <button className="icon-btn edit" onClick={() => handleEdit(task)} title="Edit">
                                      <MdEdit />
                                    </button>
                                    <button className="icon-btn delete" onClick={() => handleDelete(task.id)} title="Delete">
                                      <MdDelete />
                                    </button>
                                  </div>
                                )}
                              </div>
                              <p>{task.description}</p>
                              {task.due_date && (
                                <div className={`due-date ${isOverdue(task.due_date) ? 'overdue' : ''}`}>
                                  {isOverdue(task.due_date) && <MdWarning />}
                                  <MdCalendarToday />
                                  <span>{formatDueDate(task.due_date)}</span>
                                </div>
                              )}
                              <div className="task-meta">
                                <span className={`priority-badge priority-${task.priority}`}>
                                  {task.priority}
                                </span>
                                <span className="project-name">{task.project_name || 'No project'}</span>
                                {task.assigned_to_name && (
                                  <span className="assigned-to">👤 {task.assigned_to_name}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>

              <div className="task-column completed-column">
                <div className="column-header">
                  <div className="column-title">
                    <MdCheckCircle className="column-icon" />
                    <h3>Completed</h3>
                  </div>
                  <span className="task-count">{groupedTasks.completed.length}</span>
                </div>
                <Droppable droppableId="completed">
                  {(provided, snapshot) => (
                    <div
                      className={`task-cards ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {groupedTasks.completed.map((task, index) => (
                        <Draggable key={task.id} draggableId={`task-${task.id}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`task-card completed ${snapshot.isDragging ? 'dragging' : ''}`}
                            >
                              <div className="task-card-header">
                                <h4>{task.title}</h4>
                                {isAdmin && (
                                  <div className="task-actions">
                                    <button className="icon-btn edit" onClick={() => handleEdit(task)} title="Edit">
                                      <MdEdit />
                                    </button>
                                    <button className="icon-btn delete" onClick={() => handleDelete(task.id)} title="Delete">
                                      <MdDelete />
                                    </button>
                                  </div>
                                )}
                              </div>
                              <p>{task.description}</p>
                              {task.due_date && (
                                <div className="due-date">
                                  <MdCalendarToday />
                                  <span>{formatDueDate(task.due_date)}</span>
                                </div>
                              )}
                              <div className="task-meta">
                                <span className={`priority-badge priority-${task.priority}`}>
                                  {task.priority}
                                </span>
                                <span className="project-name">{task.project_name || 'No project'}</span>
                                {task.assigned_to_name && (
                                  <span className="assigned-to">👤 {task.assigned_to_name}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            </DragDropContext>
          </div>

          {showModal && (
            <div className="modal-overlay" onClick={handleCloseModal}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h2>{editingTask ? 'Edit Task' : 'Create New Task'}</h2>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>Task Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="4"
                    />
                  </div>
                  <div className="form-group">
                    <label>Project</label>
                    <select
                      value={formData.project_id}
                      onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    >
                      <option value="">No project</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Assign To</label>
                    <select
                      value={formData.assigned_to}
                      onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    >
                      <option value="">Unassigned</option>
                      {allUsers.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.full_name || u.username}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Priority</label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Due Date</label>
                      <input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      />
                    </div>
                  </div>
                  {editingTask && (
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="todo">To Do</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  )}
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      {editingTask ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tasks;
